import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FullWidth } from 'ui/utility/fullWidth';
import { useSortingContext } from 'utils/contexts/sorting';
import { useCustomEventsQueryContext, useFiltersContext, useSelectionContext } from '../contexts';
import { MergedCustomEvent } from '../types';
import { useCustomEventsTableEmptyState } from '../hooks';
import { groupSelectStatus, sortCustomEvents } from '../utils';
import { selectIsLoadingEvents } from '../selectors';
import { CustomEventsPagination } from './CustomEventsPagination';
import { CustomEventsTableBody } from './CustomEventsTableBody';
import { CustomEventsTableRow } from './CustomEventsTableRow';
import { CustomEventsTableHeaderRow } from './CustomEventsTableHeaderRow';

type Props = {
  customEvents: MergedCustomEvent[];
};

export const CustomEventsTable = ({ customEvents }: Props) => {
  // Search by name empty state
  const { sortBy, setSortBy, sortDirection, setSortDirection } = useSortingContext();
  const { selected, selectByIds, toggleSelection, deselectAll } = useSelectionContext();
  const [sortedEvents, setSortedEvents] = useState([] as MergedCustomEvent[]);
  const [paginatedSortedEvents, setPaginatedSortedEvents] = useState([] as MergedCustomEvent[]);
  const { isLoadingProjectSettings } = useCustomEventsQueryContext();
  const { ingestionPolicyFilter, searchQuery, startDate, endDate, preset } = useFiltersContext();
  const emptyStateConfig = useCustomEventsTableEmptyState({
    ingestionPolicyFilter,
    searchQuery,
    startDate,
    endDate,
    preset,
  });

  const isLoadingEvents = useSelector(selectIsLoadingEvents);

  useEffect(() => {
    setSortedEvents(() => sortCustomEvents(customEvents, sortBy, sortDirection));
  }, [sortBy, sortDirection, customEvents]);

  const updateSort = (name: string) => {
    if (name === sortBy) {
      const direction =
        sortDirection === SORT_DIRECTIONS.ASCENDING
          ? SORT_DIRECTIONS.DESCENDING
          : SORT_DIRECTIONS.ASCENDING;
      setSortDirection(direction);
    } else {
      setSortBy(name);
      setSortDirection(SORT_DIRECTIONS.ASCENDING);
    }
  };

  const onSelectAll = () => {
    // only select items on current page due to pagination
    const eventIds = paginatedSortedEvents.map((event) => event.eventName);
    selectByIds(eventIds);
  };
  const checkboxStatus = groupSelectStatus(selected.length, customEvents.length);
  return (
    <FullWidth>
      <CustomEventsTableHeaderRow
        checkboxStatus={checkboxStatus}
        deselectAll={deselectAll}
        onSelectAll={onSelectAll}
        sortBy={sortBy}
        sortDirection={sortDirection}
        updateSort={updateSort}
      />

      <CustomEventsTableBody
        emptyStateConfig={emptyStateConfig}
        isLoading={isLoadingEvents || isLoadingProjectSettings}
        length={paginatedSortedEvents?.length}
      >
        {paginatedSortedEvents?.map((customEvent) => (
          <CustomEventsTableRow
            key={customEvent.eventName}
            customEvent={customEvent}
            ingestionPolicyFilter={ingestionPolicyFilter}
            toggleSelection={toggleSelection}
            selected={selected}
          />
        ))}
      </CustomEventsTableBody>
      <CustomEventsPagination
        sortedEvents={sortedEvents}
        setPaginatedSortedEvents={setPaginatedSortedEvents}
      />
    </FullWidth>
  );
};
